/**
 * CurrencyAmount — displays a monetary amount in its entry currency.
 *
 * Exchange-rate direction in this system: PLN = entryAmount * exchangeRate
 * (i.e. rates are order-currency → PLN, stored as NBP daily mid-market rates).
 * When the entry currency is PLN, render a single plain PLN line.
 * When the entry currency is non-PLN, show the entry-currency amount on the
 * first line and a muted ≈ PLN equivalent with the conversion date underneath.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyLocalized } from '../utils/currency';
import { localeForLanguage } from '../utils/numberFormatting';
import { roundUnitPrice, UNIT_PRICE_DECIMALS } from '../utils/money';
import { formatDate } from '../utils/dateUtils';

interface CurrencyAmountProps {
  /** Amount expressed in `currency`. */
  amount: number | null | undefined;
  /** Entry currency code; defaults to 'PLN'. */
  currency?: string | null;
  /** Pre-computed PLN value (e.g. cost.amount). Used instead of rate*amount when provided. */
  plnAmount?: number | null;
  /** Entry-to-PLN exchange rate; used to derive plnAmount when plnAmount is not given. */
  rate?: number | null;
  /** ISO date string for the NBP rate date (cost date / order date). */
  rateDate?: string | null;
  /** Exchange-rate source, e.g. NBP_HISTORICAL or FALLBACK. */
  rateSource?: string | null;
  /** NBP table number used for the conversion. */
  rateTableNumber?: string | null;
  /** Optional text-align on the wrapper div (default: left). */
  align?: 'left' | 'right';
  /** Emphasise the primary amount (bold + dark). Use this instead of wrapping
   * the component in an inline <strong> — this renders a block <div>, so an
   * outer <strong> would be invalid block-in-inline HTML. */
  strong?: boolean;
  /**
   * 'money' (default) renders the entry amount at a fixed 2dp, matching every
   * other total/amount surface. 'unitPrice' renders roundUnitPrice(amount),
   * also 2dp (owner 2026-10-07) — pass it only when `amount` is a per-unit
   * price, not a line/order total.
   */
  variant?: 'money' | 'unitPrice';
  /**
   * Which currency is the headline. 'entry' (default) shows the entry currency
   * first with a muted ≈ PLN line. 'pln' shows PLN first with a muted ≈ entry
   * amount underneath — used in PLN-denominated summaries (e.g. financial
   * summary) so all metrics read in one currency. No effect for PLN entries.
   */
  primary?: 'entry' | 'pln';
}

const CurrencyAmount: React.FC<CurrencyAmountProps> = ({
  amount,
  currency,
  plnAmount,
  rate,
  rateDate,
  rateSource,
  rateTableNumber,
  align,
  strong,
  variant = 'money',
  primary = 'entry',
}) => {
  const { t, i18n } = useTranslation(['orders', 'transport']);
  const formatMoney = (value: number, currencyCode: string) =>
    formatCurrencyLocalized(value, currencyCode, i18n.language);
  const formatEntryAmount = (value: number, currencyCode: string) => {
    if (variant !== 'unitPrice') return formatMoney(value, currencyCode);
    try {
      // Same currency-style presentation as formatMoney, of the price rounded
      // like it is saved (2dp half-up, positive minimum 0,01).
      return new Intl.NumberFormat(localeForLanguage(i18n.language), {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: UNIT_PRICE_DECIMALS,
        maximumFractionDigits: UNIT_PRICE_DECIMALS,
      }).format(roundUnitPrice(value));
    } catch {
      return formatMoney(value, currencyCode);
    }
  };

  const wrapperStyle: React.CSSProperties | undefined = align ? { textAlign: align } : undefined;
  const primaryStyle: React.CSSProperties | undefined = strong
    ? { fontWeight: 700, color: '#0f172a' }
    : undefined;

  if (amount == null) {
    return <span>-</span>;
  }

  const cur = (currency || 'PLN').toUpperCase();

  if (cur === 'PLN') {
    return (
      <div style={{ ...wrapperStyle, ...primaryStyle }}>
        {formatEntryAmount(amount, 'PLN')}
      </div>
    );
  }

  // Compute PLN equivalent: prefer the stored value, fall back to rate calculation.
  const pln: number | null =
    plnAmount != null
      ? plnAmount
      : rate != null
      ? amount * rate
      : null;
  const rateSourceLabel = rateSource
    ? t(`transport:allocation.costProvenanceSources.${rateSource}`, {
        defaultValue: rateSource,
      })
    : null;

  const rateMeta = (
    <>
      {rateDate != null && rateDate !== '' && (
        <> &middot; {t('orders:orderDetail.rateFrom')} {formatDate(rateDate)}</>
      )}
      {rateSourceLabel && <> &middot; {rateSourceLabel}</>}
      {rateTableNumber && <> &middot; {rateTableNumber}</>}
    </>
  );
  const secondaryStyle: React.CSSProperties = { fontSize: '0.85em', color: '#64748b' };

  if (primary === 'pln' && pln != null) {
    return (
      <div style={wrapperStyle}>
        <div style={primaryStyle}>{formatMoney(pln, 'PLN')}</div>
        <div style={secondaryStyle}>
          {'≈ '}
          {formatEntryAmount(amount, cur)}
          {rateMeta}
        </div>
      </div>
    );
  }

  return (
    <div style={wrapperStyle}>
      <div style={primaryStyle}>{formatEntryAmount(amount, cur)}</div>
      {pln != null && (
        <div style={secondaryStyle}>
          {'≈ '}
          {formatMoney(pln, 'PLN')}
          {rateMeta}
        </div>
      )}
    </div>
  );
};

export default CurrencyAmount;
